/**
 * The trash guard of the automation blocks of a workspace
 * (dec-wfy-trdb-copy-mine-path-1026, dec-wfy-trdb-doc-change-path-1026):
 * first the question whether to keep a copy in Mine, then a cloud
 * automation is queued for the server, which moves the block and its config
 * in one commit. Without a connection the trash is queued and the question
 * comes before it is sent. A local workspace has no server, no copy in Mine,
 * and trashes in the doc.
 * Mount once per workspace, next to useAutomationRuntimeBridge
 * (workspace-side-effects.tsx).
 */

import { notify } from '@affine/component';
import { ContainerCopyBeforeDeleteService } from '@affine/core/components/delete-with-copy/container-copy-before-delete.service';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { I18n } from '@affine/i18n';
import { setBlockTrashGuard } from '@blocksuite/affine-block-streamlain';
import { useService } from '@toeverything/infra';
import { useEffect } from 'react';

import { AutomationDeletionService } from '../services/automation-deletion';

export function useAutomationTrashGuard(): void {
  const copy = useService(ContainerCopyBeforeDeleteService);
  const workspace = useService(WorkspaceService).workspace;
  const deletion = useService(AutomationDeletionService);

  useEffect(() => {
    const docCollection = workspace.docCollection;
    const local = workspace.flavour === 'local';
    setBlockTrashGuard(
      docCollection,
      'automation',
      async ({ store, blockId, displayName }) => {
        if (local) return 'trash-locally';
        let confirmed: boolean | null;
        try {
          confirmed = await copy.confirmFlow({
            kind: 'automation',
            sourceId: blockId,
            title: displayName,
          });
        } catch {
          notify.error({
            title: I18n.t('com.streamlain.deleteWithCopy.lookupFailed'),
          });
          return 'cancelled';
        }
        if (confirmed === false) return 'cancelled';
        await deletion.requestTrash(
          { docId: store.id, blockId },
          { copyConfirmed: confirmed === true, displayName }
        );
        return 'handed-to-server';
      }
    );
    return () => {
      setBlockTrashGuard(docCollection, 'automation', null);
    };
  }, [copy, deletion, workspace]);
}
